<template>
  <div
    class="offcanvas offcanvas-fullscreen-sm-down offcanvas-end"
    id="OffcanvasAdjustForm"
    ref="offcanvas"
    data-bs-backdrop="static"
  >
    <div class="bg-body-secondary border offcanvas-header py-2">
      {{ $t('Adjust Counted Amount') }}
      <button
        type="button"
        style="font-size: small;"
        class="btn-close shadow-none text-reset"
        data-bs-dismiss="offcanvas"
        aria-label="Close"
      />
    </div>

    <div
      class="offcanvas-body"
      v-if="loading"
    >
      <placeholder-content :is-load="true" />
    </div>
    <div
      class="offcanvas-body"
      v-else-if="!hasPermission"
    >
      <access-denied/>
    </div>
    <div
      class="offcanvas-body p-0"
      v-else
    >
      <div class="border-bottom py-2 px-3">
        <div class="d-flex justify-content-between">
          <div>
            <b style="font-size: 18px;" class="mt-2 me-1">
              {{ data.branch }}
            </b>
            <span class="badge bg-danger" style="text-transform: capitalize">
              <i class="fas fa-times-circle me-1" />
              {{ $t(data.status) }}
            </span>
            <div class="d-flex mt-0" style="font-size: 13px;">
              {{ $t('Closed At') + ': ' + formatDateTime(data.closed_at)
                + (data.closed_by? ' · ' + $t('Closed By') + ': ' + data.closed_by: '') }}
            </div>
          </div>
        </div>
      </div>

      <div class="m-3">
        <div class="row g-3">
          <div class="col-12">
            <div class="card h-100">
              <div class="card-header">
                <b>{{ '1. ' + $t('Counted Amount') }}</b>
                <!-- ยอดขาย/ยอดที่ควรมี derive จากบิลจริง จึงโชว์อย่างเดียว -->
                <div class="text-secondary" style="font-size: 13px;">
                  {{ $t('Only the counted amount can be changed. Sales and expected amounts come from actual bills.') }}
                </div>
              </div>
              <div class="card-body">
                <div class="row gx-0">
                  <div
                    class="col-12"
                    :class="[index? 'mt-3 pt-3 border-top': '']"
                    v-for="(payment, index) in payments"
                    :key="payment.id"
                  >
                    <div class="d-flex justify-content-between">
                      <label
                        :for="'actual_' + payment.id"
                        class="form-label mb-1"
                      >
                        {{ $t(payment.label) }}
                        <span
                          class="badge bg-warning-subtle text-warning-emphasis ms-1"
                          style="font-weight: 500;"
                          v-if="payment.is_adjusted"
                        >
                          <i class="fa-solid fa-clock-rotate-left me-1" />
                          {{ $t('Adjusted After Close') }}
                        </span>
                      </label>
                      <!-- สถานะเทียบกับยอดที่ควรมี — ขยับตามที่พิมพ์ทันที -->
                      <span
                        class="align-content-center"
                        style="font-size: 13px;"
                        :class="statusOf(payment)? statusOf(payment).class: 'text-secondary'"
                      >
                        <i
                          class="fas me-1"
                          :class="statusOf(payment).icon"
                          v-if="statusOf(payment)"
                        />
                        {{ (statusOf(payment)? $t(statusOf(payment).key) + ' ': '')
                          + '(' + $t('System') + ': ' + $storeIndex.formatThaiCurrency(payment.expected_amount) + ')' }}
                      </span>
                    </div>
                    <div class="input-group-sm">
                      <input
                        :id="'actual_' + payment.id"
                        type="number"
                        inputmode="decimal"
                        style="font-size: 16px;"
                        class="form-control shadow-none text-end"
                        :class="[isChanged(payment)? 'border-warning': '']"
                        v-model.trim="payment.actual_amount"
                      >
                    </div>
                    <div class="d-flex justify-content-between mt-1" style="font-size: 12px;">
                      <span class="text-secondary">
                        {{ $t('Sales') + ': ' + $storeIndex.formatThaiCurrency(payment.sales_amount) }}
                      </span>
                      <span :class="[isChanged(payment)? 'text-warning-emphasis': 'text-secondary']">
                        {{ $t('Was') + ': ' + (payment.original_actual === ''? $t('Not Counted'): $storeIndex.formatThaiCurrency(payment.original_actual)) }}
                        <template v-if="isChanged(payment)">
                          <i class="fa-solid fa-arrow-right mx-1" />
                          <b>{{ $storeIndex.formatThaiCurrency(payment.actual_amount) }}</b>
                        </template>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="col-12">
            <div class="card h-100">
              <div class="card-header">
                <b>
                  {{ '2. ' + $t('Reason') }}
                  <span class="text-danger">*</span>
                </b>
              </div>
              <div class="card-body">
                <textarea
                  class="form-control shadow-none"
                  id="reason"
                  rows="3"
                  v-model.trim="v$.form.reason.$model"
                  style="font-size: 14px;"
                  :placeholder="$t('e.g. miscounted the drawer') + '...'"
                />
                <div v-for="error of v$.form.reason.$errors"
                  class="d-block invalid-feedback"
                  :key="error.$uid"
                >
                  {{ error.$message }}
                </div>
                <div class="text-secondary mt-2" style="font-size: 12px;">
                  <i class="fa-solid fa-circle-info me-1" />
                  {{ $t('This shift is already closed. The change will be recorded with your name.') }}
                </div>
              </div>
            </div>
          </div>

          <!-- เคยถูกแก้มาแล้วกี่ครั้ง ใครแก้ เพราะอะไร -->
          <div class="col-12" v-if="history.length">
            <div class="card h-100">
              <div class="card-header">
                <b>
                  <i class="fa-solid fa-clock-rotate-left me-1" />
                  {{ $t('Adjustment History') }}
                </b>
              </div>
              <div class="card-body py-2">
                <div
                  class="border-start border-warning border-3 ps-2 py-1"
                  :class="[index? 'mt-2': '']"
                  style="font-size: 12px;"
                  v-for="(item, index) in history"
                  :key="index"
                >
                  <div>
                    <b>{{ $t(item.label) }}</b>
                    {{ ' ' + (item.old_actual === ''? $t('Not Counted'): $storeIndex.formatThaiCurrency(item.old_actual)) }}
                    <i class="fa-solid fa-arrow-right mx-1" />
                    <b>{{ $storeIndex.formatThaiCurrency(item.new_actual) }}</b>
                  </div>
                  <div class="text-secondary">
                    {{ item.adjusted_by + ' · ' + formatDateTime(item.adjusted_at) }}
                  </div>
                  <div class="text-secondary fst-italic">{{ item.reason }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      class="bg-light border-top justify-content-center modal-footer py-2"
      v-if="!loading && hasPermission"
    >
      <ladda
        text="Save"
        :btn-class="'btn btn-primary btn-block '"
        :loading="loadingSave"
        icon="<i class='fa-solid fa-floppy-disk'></i>"
        :disabled="v$.$invalid || !changedPayments.length"
        @click="handleAdjustShift"
      />
      <button
        type="button"
        data-bs-dismiss="offcanvas"
        class="border-dark-subtle btn-sm btn btn-light me-2 ms-2"
      >
        {{ $t('Cancel') }}
      </button>
    </div>
  </div>
</template>

<script lang="js">
import PlaceholderContent from '@/plugins/PlaceholderContent.vue';
import AccessDenied from "@/plugins/AccessDenied.vue";
import Ladda from '@/plugins/Ladda.vue';
import { useVuelidate } from '@vuelidate/core';
import validation from "./assets/template/validation-adjust";
import { Shifts } from '@/services/endpoint/pos.service';
import moment from "moment";

export default {
  setup: () => ({ v$: useVuelidate() }),
  validationConfig: {
    $lazy: true,
  },
  validations() {
    return validation(this.form)
  },
  props: {
    id: {
      type: Number,
      default: 0
    },
  },
  data() {
    return {
      loading: true,
      hasPermission: false,
      loadingSave: false,
      data: {},
      payments: [],
      form: {
        reason: ''
      }
    }
  },
  components: {
    PlaceholderContent,
    Ladda,
    AccessDenied,
  },
  methods: {
    formatDateTime(value) {
      return value? moment(value).format('YYYY-MM-DD HH:mm:ss'): '-';
    },
    // "เปลี่ยน" = มีค่า และค่าไม่เท่าเดิม (ช่องที่ตอนปิดกะไม่ได้นับ = กรอกอะไรก็ถือว่าเปลี่ยน)
    isChanged(payment) {
      if (payment.actual_amount === '' || payment.actual_amount === null) return false;
      if (payment.original_actual === '') return true;
      return Number(payment.actual_amount) !== Number(payment.original_actual);
    },
    // เทียบกับยอดที่ควรมี ไม่ใช่ยอดเดิม — คนแก้ต้องเห็นว่าแก้แล้วยังขาด/เกินอยู่ไหม
    statusOf(payment) {
      if (payment.actual_amount === '' || payment.actual_amount === null) return null;
      let diff = Number(payment.actual_amount) - Number(payment.expected_amount);
      if (Number.isNaN(diff)) return null;
      if (diff < 0) return { key: 'Short', class: 'text-danger', icon: 'fa-times-circle' };
      if (diff > 0) return { key: 'Over', class: 'text-info', icon: 'fa-circle-info' };
      return { key: 'Match', class: 'text-secondary', icon: 'fa-circle-check' };
    },
    async fetchData() {
      await Shifts.getAdjust(this.id).then(res => {
        this.data = res.data.data;
        // เก็บยอดตั้งต้นไว้เทียบ — ส่งเฉพาะแถวที่เปลี่ยนจริง ไม่ยิงทั้งตาราง
        this.payments = (res.data.data.payment_breakdown ?? []).map(payment => ({
          id: payment.id,
          label: payment.label,
          sales_amount: payment.sales_amount,
          expected_amount: payment.expected_amount,
          actual_amount: payment.actual_amount,
          original_actual: payment.actual_amount,
          is_adjusted: payment.is_adjusted,
          adjustments: payment.adjustments ?? []
        }));
        this.hasPermission = true;
      }).catch(err => {
        this.hasPermission = false;
        this.loading = false;
        throw err;
      });
    },
    // payload สร้างเองทีละ key — API ปฏิเสธ key ที่ไม่ได้อยู่ในลิสต์ (ดู CONVENTIONS)
    setParamsBeforeSendAdjustShift() {
      return {
        reason: this.form.reason,
        payments: this.changedPayments.map(payment => ({
          shift_payment_total_id: payment.id,
          actual_amount: Number(payment.actual_amount)
        }))
      };
    },
    async handleAdjustShift() {
      this.v$.$touch();
      if (this.v$.$invalid || !this.changedPayments.length) return;

      let self = this;
      let changes = this.changedPayments.map(payment =>
        self.$t(payment.label) + ': ' +
        (payment.original_actual === ''? self.$t('Not Counted'): self.$storeIndex.formatThaiCurrency(payment.original_actual)) +
        ' → ' + self.$storeIndex.formatThaiCurrency(payment.actual_amount)
      ).join('<br>');

      this.$swal({
        icon: "warning",
        iconColor: "#FF7043",
        title: this.$t("Adjust Counted Amount") + '?',
        html: changes + '<br><br>' + this.$t("This shift is already closed. The change will be recorded with your name."),
        showCancelButton: true,
        cancelButtonText: this.$t("Cancel"),
        confirmButtonText: '<i class="fas fa-check-circle me-1"></i>' + this.$t("Confirm"),
        buttonsStyling: false,
        customClass: { confirmButton: "btn btn-primary me-2", cancelButton: "btn btn-danger" },
        focusConfirm: false,
        showLoaderOnConfirm: true,
        preConfirm: async function () {
          return Shifts.adjust(self.id, self.setParamsBeforeSendAdjustShift())
            .then(() => {
              self.$parent.fetchData();
              self.$toast.success("<B>" + self.$t('Success') + "</B>\n" + self.$t('Adjust Success!'));
              $(self.$refs.offcanvas).offcanvas('hide');
            })
            .catch((error) => {
              if (error?.response?.data?.code === "VALIDATION") {
                self.$swal.showValidationMessage(self.$t(error?.response?.data?.message))
              } else {
                self.$toast.warn(error?.response?.data?.message);
              }
            })
        },
      });
    },
  },
  async mounted() {
    $(this.$refs.offcanvas).offcanvas('show');

    $(this.$refs.offcanvas).on('shown.bs.offcanvas', async () => {
      this.loading = true;
      try {
        await this.fetchData();
        this.loading = false;
      } catch (err) {
      }
    });

    $(this.$refs.offcanvas).on('hidden.bs.offcanvas', () => {
      this.$emit('close');
    });
  },
  computed: {
    changedPayments() {
      return this.payments.filter(payment => this.isChanged(payment));
    },
    history() {
      return this.payments.flatMap(payment =>
        (payment.adjustments ?? []).map(item => ({ ...item, label: payment.label }))
      ).sort((a, b) => new Date(b.adjusted_at) - new Date(a.adjusted_at));
    }
  }
}
</script>
